import { useEffect, useState, type FC } from "react";
import {
  CircleMarker,
  MapContainer,
  TileLayer,
  Tooltip,
  useMap,
  useMapEvents,
} from "react-leaflet";
import { BLACK_MARBLE_TEMPLATE } from "../../lib/nightLights";
import type { Site } from "../../types";

interface DarkSkyMapProps {
  sites: Site[];
  selectedId: string | null;
  overlayOpacity: number;
  onSelectSite: (site: Site) => void;
  onInspect: (lat: number, lng: number) => void;
  flyTo: { latitude: number; longitude: number; zoom?: number } | null;
  inspect: { latitude: number; longitude: number } | null;
}

function bortlePath(bortle: number, selected: boolean) {
  if (selected) {
    return {
      color: "#FA5468",
      fillColor: "#D32A46",
      fillOpacity: 0.9,
      weight: 3,
    };
  }
  if (bortle <= 2) {
    return {
      color: "#68AAA0",
      fillColor: "#2D92A4",
      fillOpacity: 0.7,
      weight: 1.5,
    };
  }
  if (bortle === 3) {
    return {
      color: "#F6BB51",
      fillColor: "#E2991A",
      fillOpacity: 0.7,
      weight: 1.5,
    };
  }
  return {
    color: "#FA5468",
    fillColor: "#D32A46",
    fillOpacity: 0.55,
    weight: 1.5,
  };
}

function SetupMap() {
  const map = useMap();

  if (!map.getPane("night-lights")) {
    const lights = map.createPane("night-lights");
    lights.style.zIndex = "350";
    lights.style.pointerEvents = "none";
  }
  if (!map.getPane("map-labels")) {
    const labels = map.createPane("map-labels");
    labels.style.zIndex = "450";
    labels.style.pointerEvents = "none";
  }

  useEffect(() => {
    const invalidate = () => map.invalidateSize();
    invalidate();
    const timer = window.setTimeout(invalidate, 100);
    window.addEventListener("resize", invalidate);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("resize", invalidate);
    };
  }, [map]);

  return null;
}

function FlyTo({
  latitude,
  longitude,
  zoom,
}: {
  latitude: number;
  longitude: number;
  zoom: number;
}) {
  const map = useMap();

  useEffect(() => {
    map.flyTo([latitude, longitude], zoom, { duration: 0.7 });
  }, [latitude, longitude, zoom, map]);

  return null;
}

function ClickCatcher({
  onInspect,
}: {
  onInspect: (lat: number, lng: number) => void;
}) {
  useMapEvents({
    click(event) {
      const target = event.originalEvent.target as HTMLElement | null;
      if (target?.closest?.(".leaflet-interactive")) {
        return;
      }
      onInspect(event.latlng.lat, event.latlng.lng);
    },
  });
  return null;
}

const DarkSkyMap: FC<DarkSkyMapProps> = ({
  sites,
  selectedId,
  overlayOpacity,
  onSelectSite,
  onInspect,
  flyTo,
  inspect,
}) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return <div style={{ width: "100%", height: "100%", background: "#0b1213" }} />;
  }

  return (
    <MapContainer
      center={[32.2, -106.5]}
      zoom={5}
      minZoom={3}
      maxZoom={12}
      scrollWheelZoom
      worldCopyJump={false}
      style={{ width: "100%", height: "100%", background: "#0b1213" }}
    >
      <SetupMap />
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
        url="https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png"
      />
      {overlayOpacity > 0 && (
        <TileLayer
          attribution='Night lights: NASA Black Marble / <a href="https://earthdata.nasa.gov/data/projects/black-marble">GIBS</a>'
          url={BLACK_MARBLE_TEMPLATE}
          opacity={overlayOpacity}
          maxNativeZoom={8}
          maxZoom={12}
          pane="night-lights"
        />
      )}
      <TileLayer
        url="https://{s}.basemaps.cartocdn.com/dark_only_labels/{z}/{x}/{y}{r}.png"
        pane="map-labels"
      />
      {sites.map((site) => {
        const selectedFeature = site.id === selectedId;
        return (
          <CircleMarker
            key={site.id}
            center={[site.latitude, site.longitude]}
            radius={selectedFeature ? 11 : 8}
            pathOptions={bortlePath(site.bortle, selectedFeature)}
            eventHandlers={{
              click: () => onSelectSite(site),
            }}
          >
            <Tooltip direction="top" offset={[0, -4]}>
              <span className="font-semibold">{site.shortName}</span>
              <span> · Bortle {site.bortle}</span>
            </Tooltip>
          </CircleMarker>
        );
      })}
      {inspect && (
        <CircleMarker
          center={[inspect.latitude, inspect.longitude]}
          radius={6}
          pathOptions={{
            color: "#F6BB51",
            fillColor: "#F6BB51",
            fillOpacity: 0.9,
            weight: 2,
          }}
        />
      )}
      {flyTo && (
        <FlyTo
          latitude={flyTo.latitude}
          longitude={flyTo.longitude}
          zoom={flyTo.zoom ?? 8}
        />
      )}
      <ClickCatcher onInspect={onInspect} />
    </MapContainer>
  );
};

export default DarkSkyMap;
